<script setup>
import { onMounted, computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";

import Filters from "@/components/academy/filters/Filters.vue";

import debounce from "lodash/debounce";
import { useAcademyStore } from "@/stores";

const academyStore = useAcademyStore();

const router = useRouter();

const data = computed(() => academyStore.overview);

const registeredSum = computed(() => academyStore.registeredBeneficiariesSum);
const preRegisteredSum = computed(() => academyStore.preRegisteredBeneficiariesSum);
const completedSum = computed(() => academyStore.completedBeneficiariesSum);

const params = computed(() => academyStore.requestQueryParams);

const pagination = computed(() => academyStore.pagination);

const isFetching = ref(false);

const formatDateTime = (date) => {
  if (date === null) {
    return date;
  }
  return moment(date, "YYYY-MM-DDTHH:mm:ss").format("DD-MM-YYYY HH:mm");
};

const getCourses = async () => {
  isFetching.value = true;

  params.value.set("page", pagination.value.currentPage - 1);
  params.value.set("size", pagination.value.pageSize);

  await academyStore.getAcademyOverview(params.value);

  isFetching.value = false;
};

const checkClickEvent = (event) => {
  const check = event.target && event.target.classList.contains("mdi-pencil");
  return check;
};

const edit = (item, event) => {
  const check = checkClickEvent(event);
  if (check) {
    router.push({
      path: `/academy/edit/${item}`,
    });
  }
};

const download = ref(false);

const exportTable = async () => {
  download.value = true;
  const resp = await academyStore.downloadExcel(params.value);

  const url = window.URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "courses.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  download.value = false;
};

const coursesDebounced = debounce(getCourses, 500);

watch(
  pagination,
  () => {
    if (!isFetching.value) {
      getCourses();
    }
  },
  {
    deep: true,
  }
);

watch(
  params,
  () => {
    coursesDebounced();
  },
  {
    deep: true,
  }
);

const headers = [
  {
    title: "Actions",
    key: "actions",
    align: "center",
    sortable: false,
    fixed: true,
  },
  { title: "Start Date", key: "startDate", width: "300px" },
  { title: "End Date", key: "endDate", width: "300px" },
  { title: "Training Cycle", key: "cycle", width: "200px" },
  { title: "Odyssea Projects", key: "projects" },
  { title: "Odyssea Branch", key: "branch" },
  { title: "Status", key: "courseStatus" },
  { title: "Training Course", key: "trainingCourse" },
  { title: "Thematic Area", key: "thematicArea" },
  { title: "Type", key: "type" },
  { title: "Hours", key: "hours" },
  {
    title: "Pre-Registered Beneficiaries",
    key: "preregisteredBeneficiaries",
    width: "300px",
  },
  { title: "Registered Beneficiaries", key: "registeredBeneficiaries" },
  { title: "Completed Beneficiaries", key: "completedBeneficiaries" },
];

// onBeforeMount(() => {
//   academyStore.resetAction();
// });

onMounted(() => {
  getCourses();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-10 page-title">Academy Overview</h1>
        <Filters />
        <div class="d-flex justify-space-between align-center mb-5">
          <h3>Total: {{ pagination.totalItems }}</h3>
          <v-btn
            color="academy"
            :loading="download"
            :disabled="download"
            @click="exportTable()"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-file-excel</v-icon>
              <span>Export</span>
            </div>
          </v-btn>
        </div>
        <v-data-table
          :items="data"
          :headers="headers"
          :items-per-page="pagination.pageSize"
          :hide-default-footer="true"
          :loading="isFetching"
          class="courses-table bordered-columns"
        >
          <template #item.cycle="{ item }">
            <router-link
              :to="'/academy/view/' + item.id"
              class="chip-link-color-academy"
            >
              <v-chip> {{ item.cycle }} </v-chip>
            </router-link>
          </template>

          <template #item.project="{ item }">
            <v-chip v-if="item.project">
              {{ item.project }}
            </v-chip>
          </template>

          <template #item.startDate="{ item }">
            <span style="white-space: nowrap">{{
              formatDateTime(item.startDate)
            }}</span>
          </template>

          <template #item.endDate="{ item }">
            <span style="white-space: nowrap">{{
              formatDateTime(item.endDate)
            }}</span>
          </template>

          <template #item.projects="{ item }">
            <v-chip
              v-for="project in item.projects"
              :key="project.id"
              class="v-chip--variant-tonal mb-1 mt-1"
              style="white-space: nowrap"
            >
              {{ project.name }}
            </v-chip>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                color="academy"
                @click="edit(item.id, $event)"
              />
            </div>
          </template>
          <template #body.append>
            <tr>
              <th colspan="10"/>
              <th> Total </th>
              <th> {{ preRegisteredSum }}</th>
              <th> {{ registeredSum }}</th>
              <th> {{ completedSum }}</th>
            </tr>
          </template>
        </v-data-table>
        <div class="mt-5">
          <v-pagination
            v-model="pagination.currentPage"
            :length="pagination.pageCount"
            :total-visible="5"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-academy));
}
.v-table {
  table-layout: fixed;

  td,
  th {
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    white-space: normal;
  }
}
</style>
